import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import { getBusinessUnits } from 'lib/api/business';
import { searchOrCreateBusiness } from 'lib/api/business';
import { searchOrCreateBusinessExpress } from 'lib/api/express';
import CustomButton from 'lib/designSystem/Button';
import { validaRut } from 'lib/functions/utils';
import { Business, BusinessSummary, BusinessUnit } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import BusinessDetailsModal from './components/BusinessDetailsModal';
import BusinessTooltip from './components/BusinessTooltip';

interface BusinessInputProps {
  identifier: string | null;
  unitCode: string | null;
  onChange: (identifier: string | null, unitCode: string | null) => void;
  label?: string;
  required?: boolean;
  express?: boolean;
}

interface SelectedBusiness {
  business: Business;
  summary?: BusinessSummary;
  unit?: BusinessUnit;
}

const BusinessInput: React.FC<BusinessInputProps> = ({
  identifier,
  unitCode,
  onChange,
  label = 'Mandante',
  required,
  express = false,
}) => {
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchIdentifier, setSearchIdentifier] = useState('');
  const [inputValue, setInputValue] = useState(identifier ?? '');
  const [rutError, setRutError] = useState<string | null>(null);
  const [isRutValid, setIsRutValid] = useState(true);
  const [selectedBusiness, setSelectedBusiness] =
    useState<SelectedBusiness | null>(null);

  const handleSearch = () => {
    if (inputValue) {
      setSearchIdentifier(inputValue);
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleBusinessSelect = (
    business: Business,
    summary?: BusinessSummary,
    unit?: BusinessUnit,
  ) => {
    setSelectedBusiness({ business, summary, unit });
    setInputValue('');
  };

  const handleRemoveBusiness = () => {
    setSelectedBusiness(null);
    setInputValue('');
    onChange(null, null);
  };

  const getDisplayValue = () => {
    if (!selectedBusiness) return identifier;
    return `${selectedBusiness.business.name} (${selectedBusiness.business.identifier})`;
  };

  const getTooltipContent = () => {
    if (!selectedBusiness) return null;

    return (
      <BusinessTooltip
        business={selectedBusiness.business}
        summary={selectedBusiness.summary}
        unit={selectedBusiness.unit}
      />
    );
  };

  const loadBusinessData = async (businessIdentifier: string) => {
    try {
      setIsLoading(true);
      const domain = window.location.hostname;
      const secretId = domain.split('.')[0];

      let businessRes;
      if (express) {
        businessRes = await searchOrCreateBusinessExpress(
          dispatch,
          secretId,
          businessIdentifier,
        );
      } else {
        businessRes = await searchOrCreateBusiness(
          dispatch,
          businessIdentifier,
        );
      }

      if (businessRes) {
        let units = [];
        try {
          units = await getBusinessUnits(
            dispatch,
            businessRes.business.identifier,
          );
        } catch (error) {
          console.error('No business units found');
        }

        let selectedUnit = undefined;
        if (unitCode && units.length > 0) {
          selectedUnit = units.find(
            (unit: BusinessUnit) => unit.unitCode === unitCode,
          );
        } else if (units.length > 0) {
          selectedUnit = units[0];
        }

        setSelectedBusiness({
          business: businessRes.business,
          summary: businessRes.summary,
          unit: selectedUnit,
        });
      }
    } catch (error) {
      console.error('Error loading business data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (identifier && !selectedBusiness) {
      void loadBusinessData(identifier);
    }
  }, [identifier, unitCode]);

  useEffect(() => {
    if (!selectedBusiness) return;
    onChange(
      selectedBusiness.business.identifier,
      selectedBusiness.unit?.unitCode ?? null,
    );
  }, [selectedBusiness, onChange]);

  useEffect(() => {
    if (inputValue) {
      if (!validaRut(inputValue)) {
        setRutError('RUT inválido');
        setIsRutValid(false);
      } else {
        setRutError(null);
        setIsRutValid(true);
      }
    } else {
      setRutError(null);
      setIsRutValid(true);
    }
  }, [inputValue]);

  useEffect(() => {
    setInputValue(identifier ?? '');
  }, [identifier]);

  return (
    <>
      {identifier ? (
        <Tooltip
          title={getTooltipContent()}
          placement="top-start"
          arrow
          enterDelay={300}
          leaveDelay={200}
          PopperProps={{
            sx: {
              '& .MuiTooltip-tooltip': {
                backgroundColor: 'white',
                color: 'text.primary',
                fontSize: '0.75rem',
                maxWidth: 320,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                border: '1px solid #e0e0e0',
                padding: 0,
              },
              '& .MuiTooltip-arrow': {
                color: 'white',
              },
            },
          }}
        >
          <TextField
            label={label}
            value={getDisplayValue()}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  {isLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={handleRemoveBusiness}
                      color="error"
                      aria-label="Remove business"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            fullWidth
            required={required}
          />
        </Tooltip>
      ) : (
        <TextField
          label={label}
          value={inputValue}
          onChange={(e) => {
            const newValue = e.target.value;
            setInputValue(newValue);
          }}
          placeholder="12345678-9"
          required={required}
          error={!!rutError}
          helperText={rutError}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box>
                  {isLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CustomButton
                      color="primary"
                      size="small"
                      onClick={handleSearch}
                      disabled={
                        !inputValue || !isRutValid || !validaRut(inputValue)
                      }
                    >
                      <SearchIcon fontSize="small" />
                    </CustomButton>
                  )}
                </Box>
              </InputAdornment>
            ),
          }}
          fullWidth
        />
      )}
      <BusinessDetailsModal
        open={modalOpen}
        onClose={handleModalClose}
        identifier={searchIdentifier}
        onSelect={handleBusinessSelect}
        express={express}
      />
    </>
  );
};

export default BusinessInput;
